import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import DatePicker from '../../components/shared/DatePicker/DatePicker';
import Selector from '../../components/shared/Selector/Selector';
import { ServiceTasks } from '../../services/service-tasks';
import { NotificationManager } from 'react-notifications';
import appState from '../../state/AppStateContainer';

export type CreateActivityFromMessageProps = {
  title: string;
  description: string;
  projectId: string;
  projectLabel: string;
  onClose: () => void;
};

type AssigneeOption = { value: string; label: string };

const CreateActivityFromMessageModal: FC<CreateActivityFromMessageProps> = ({
  title: initialTitle,
  description: initialDescription,
  projectId,
  projectLabel,
  onClose,
}) => {
  const [t] = useTranslation('dashboard');
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [dueDate, setDueDate] = useState<Date | null>(moment().add(1, 'day').toDate());
  const [submitting, setSubmitting] = useState(false);
  const [errorMessages, setErrorMessages] = useState<Record<string, string | undefined>>({});

  const statusOption = { value: 'inProgress', label: t('messages.statusInProgress', 'Pågående') };
  const projectOption = { value: projectId, label: projectLabel };

  const userDetail = appState.getLoggedInUserDetail();
  const currentUserOption: AssigneeOption = { value: userDetail?.id ?? '', label: userDetail?.name || '' };

  // Assignable users = members of this project (same organization, added to the
  // project) — fetched the same way the Tasks page builds its assignee dropdown
  // (ServiceTasks.getProjectUsers). Defaults to the logged-in user; falls back to
  // just the current user if the fetch fails or returns nobody.
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([currentUserOption]);
  const [selectedAssignee, setSelectedAssignee] = useState<AssigneeOption>(currentUserOption);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const users = await ServiceTasks.getProjectUsers(projectId);
      if (cancelled || !Array.isArray(users) || users.length === 0) return;
      const options: AssigneeOption[] = users.map((u: { _id: string; name: string }) => ({ value: u._id, label: u.name }));
      setAssigneeOptions(options);
      const mine = options.find(o => o.value === userDetail?.id);
      setSelectedAssignee(mine ?? options[0]);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleSubmit = async () => {
    const errors: Record<string, string | undefined> = {};
    if (!title.trim()) errors.title = t('messages.errorRequired', 'Obligatoriskt fält');
    if (!description.trim()) errors.description = t('messages.errorRequired', 'Obligatoriskt fält');
    if (!dueDate) errors.dueDate = t('messages.errorRequired', 'Obligatoriskt fält');
    if (!selectedAssignee?.value) errors.assignee = t('messages.errorRequired', 'Obligatoriskt fält');

    if (Object.values(errors).some(Boolean)) {
      setErrorMessages(errors);
      return;
    }

    setSubmitting(true);
    try {
      await ServiceTasks.addTasks({
        title,
        description,
        dueDate,
        projectId,
        assignTo: selectedAssignee.value,
        status: statusOption.value,
      });
      NotificationManager.success(
        t('messages.activityCreated', 'Aktivitet skapad'),
        t('messages.saved', 'Sparad')
      );
      onClose();
    } catch {
      // keep modal open on error
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('messages.createActivity', 'Skapa aktivitet')}
      show={true}
      setShow={(val) => { if (!val) onClose(); }}
      buttonInfo={{
        label: t('messages.createActivity', 'Skapa aktivitet'),
        action: handleSubmit,
        loaderShow: submitting,
      }}
    >
      <div className="activityModalContainer">
        <Field
          label={t('messages.fieldTitle', 'Namn för aktivitet')}
          required
          value={title}
          onChange={(value: string) => {
            setTitle(value);
            setErrorMessages(prev => ({ ...prev, title: undefined }));
          }}
          errorMessage={errorMessages.title}
          style={{ fontSize: '14px' }}
        />
        <Field
          label={t('messages.fieldDescription', 'Beskrivning av aktivitet')}
          required
          value={description}
          onChange={(value: string) => {
            setDescription(value);
            setErrorMessages(prev => ({ ...prev, description: undefined }));
          }}
          errorMessage={errorMessages.description}
          multiline
          style={{ fontSize: '14px' }}
        />
        <DatePicker
          minDate={new Date()}
          label={t('messages.fieldDueDate', 'Förfallodatum')}
          required
          selected={dueDate ?? undefined}
          onChange={(date: Date) => {
            setDueDate(date);
            setErrorMessages(prev => ({ ...prev, dueDate: undefined }));
          }}
          errorMessage={errorMessages.dueDate}
          style={{ fontSize: '14px' }}
        />
        <Selector
          label={t('messages.fieldProject', 'Projekt')}
          required
          options={[projectOption]}
          value={projectOption}
          isDisabled
        />
        <Selector
          label={t('messages.fieldAssignee', 'Tilldela aktivitet')}
          required
          options={assigneeOptions}
          value={selectedAssignee}
          onChange={(value) => {
            if (!value) return;
            setSelectedAssignee(value);
            setErrorMessages(prev => ({ ...prev, assignee: undefined }));
          }}
          errorMessage={errorMessages.assignee}
        />
        <Selector
          label={t('messages.fieldStatus', 'Status')}
          required
          options={[statusOption]}
          value={statusOption}
          isDisabled
        />
      </div>
    </Modal>
  );
};

export default CreateActivityFromMessageModal;
